import {
  Children,
  forwardRef,
  isValidElement,
  type ComponentPropsWithoutRef,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

import { type PanelWidth } from './width-utils.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';

/** @public */
export interface ResizablePanelProps
  extends PropsWithChildren,
    BehaviorTrackingProps,
    AriaLabelingProps,
    StylingProps,
    MaskingProps,
    DataTestId,
    Pick<ComponentPropsWithoutRef<'div'>, 'id'> {
  /** The minimum width of the panel. Accepts a pixel value or a percentage string. */
  minWidth?: PanelWidth;
  /** The maximum width of the panel. Accepts a pixel value or a percentage string. */
  maxWidth?: PanelWidth;
  /** The initial width of the panel. Accepts a pixel value or a percentage string. */
  defaultWidth?: PanelWidth;
  /**
   * Whether the panel width can be resized by dragging.
   * @defaultValue true
   */
  resizable?: boolean;
  /**
   * The position of the panel relative to the content.
   * @defaultValue 'start'
   */
  position?: 'start' | 'end';
  /** Callback triggered when the panel is resized, with the new width in pixels. */
  onResize?: (width: number) => void;
  /** Accessible name for the resize separator. Referenced by aria-label on the separator element. */
  label?: string;
}

/** @public */
export const PanelSlot = forwardRef<HTMLDivElement, ResizablePanelProps>(
  (_props, _ref) => null,
);

/** @public */
export interface ContentProps
  extends PropsWithChildren,
    BehaviorTrackingProps,
    AriaLabelingProps,
    StylingProps,
    MaskingProps,
    DataTestId {
  /** The minimum width of the content area. Accepts a pixel value or a percentage string. */
  minWidth?: PanelWidth;
}

export const ContentSlot = forwardRef<HTMLDivElement, ContentProps>(
  (_props, _ref) => null,
);

/** @internal */
export interface ResizeHandleProps
  extends PropsWithChildren,
    BehaviorTrackingProps,
    AriaLabelingProps,
    StylingProps,
    MaskingProps,
    DataTestId {}
/**
 * Optional slot to render custom decorations inside the resize handle area.
 *
 * Note: The resize handle column is fixed to 8px to keep layout stable.
 * Content rendered here does not affect layout width; it may visually overflow.
 *
 * @internal
 */
export const ResizeHandleSlot = forwardRef<HTMLDivElement, ResizeHandleProps>(
  (_props, _ref) => null,
);

export interface ExtractedPanelSlot {
  children?: ReactNode;
  config: {
    minWidth: PanelWidth;
    maxWidth: PanelWidth;
    defaultWidth: PanelWidth;
    position: 'start' | 'end';
    resizable: boolean;
    onResize?: (width: number) => void;
    label?: string;
  };
  domProps: ComponentPropsWithoutRef<'div'>;
  ref?: Ref<HTMLDivElement>;
}

export interface ExtractedContentSlot {
  children?: ReactNode;
  config: {
    minWidth: PanelWidth;
  };
  domProps: ComponentPropsWithoutRef<'div'>;
  ref?: Ref<HTMLDivElement>;
}

export interface ExtractedResizeHandleSlot {
  children?: ReactNode;
  domProps: ComponentPropsWithoutRef<'div'>;
  ref?: Ref<HTMLDivElement>;
}

function isPanelSlot(
  element: ReactElement,
): element is ReactElement<ResizablePanelProps> {
  return element.type === PanelSlot;
}

function isContentSlot(
  element: ReactElement,
): element is ReactElement<ContentProps> {
  return element.type === ContentSlot;
}

function isResizeHandleSlot(
  element: ReactElement,
): element is ReactElement<ResizeHandleProps> {
  return element.type === ResizeHandleSlot;
}

/**
 * Extracts only props and children from the slots, to ensure a stable slot component to render in the root
 */
export function extractSlotsProps(children: ReactNode): {
  panel: ExtractedPanelSlot;
  content: ExtractedContentSlot | null;
  resizeHandle: ExtractedResizeHandleSlot | null;
} {
  let panel: ExtractedPanelSlot | null = null;
  let content: ExtractedContentSlot | null = null;
  let resizeHandle: ExtractedResizeHandleSlot | null = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }

    if (isPanelSlot(child)) {
      if (panel) {
        throw new Error('SplitLayout: only one Panel slot is allowed');
      }

      const {
        minWidth,
        maxWidth,
        defaultWidth,
        position,
        resizable,
        children,
        onResize,
        label,
        ...rest
      } = child.props;

      panel = {
        children,
        config: {
          minWidth: minWidth ?? 200,
          maxWidth: maxWidth ?? Infinity,
          defaultWidth: defaultWidth ?? 200,
          position: position ?? 'start',
          resizable: resizable ?? true,
          onResize,
          label,
        },
        domProps: rest,
        ref: 'ref' in child ? (child.ref as Ref<HTMLDivElement>) : undefined,
      };
    } else if (isContentSlot(child)) {
      if (content) {
        throw new Error('SplitLayout: only one Content slot is allowed');
      }

      const { children, minWidth, ...rest } = child.props;

      content = {
        children,
        config: {
          minWidth: minWidth ?? 250,
        },
        domProps: rest,
        ref: 'ref' in child ? (child.ref as Ref<HTMLDivElement>) : undefined,
      };
    } else if (isResizeHandleSlot(child)) {
      if (resizeHandle) {
        throw new Error('SplitLayout: only one ResizeHandle slot is allowed');
      }

      const { children, ...rest } = child.props;

      resizeHandle = {
        children,
        domProps: rest,
        ref: 'ref' in child ? (child.ref as Ref<HTMLDivElement>) : undefined,
      };
    }
  });

  if (!panel) {
    throw new Error('Panel slot is required');
  }

  return { panel, content, resizeHandle };
}
